import { createParser } from 'eventsource-parser';

import type { ChatStreamEvent } from 'entities/chat/types';

type RawFrame = { type?: string; data?: unknown };

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

// Map a backend `{type, data}` frame onto a typed domain event, dropping anything malformed.
function toEvent(frame: RawFrame): ChatStreamEvent | null {
  const data = asRecord(frame.data);

  switch (frame.type) {
    case 'thread': {
      const threadId = str(data.thread_id);
      return threadId ? { type: 'thread', threadId } : null;
    }
    case 'text-delta':
      return typeof frame.data === 'string' ? { type: 'text-delta', text: frame.data } : null;
    case 'tool-call': {
      const id = str(data.id);
      const tool = str(data.tool);
      return id && tool ? { type: 'tool-call', id, tool } : null;
    }
    case 'tool-result': {
      const id = str(data.id);
      return id ? { type: 'tool-result', id } : null;
    }
    case 'follow-up': {
      const question = str(data.question);
      const prompt = str(data.prompt);
      return question && prompt ? { type: 'follow-up', question, prompt } : null;
    }
    case 'confirmation': {
      const previewToken = str(data.preview_token);
      const env = str(data.env);
      if (!(previewToken && env)) {
        return null;
      }
      return {
        type: 'confirmation',
        toolCallId: str(data.tool_call_id) ?? '',
        // An older backend sends no preview_type; a new order is what it only ever meant.
        previewType: str(data.preview_type) === 'refresh' ? 'refresh' : 'new_order',
        previewToken,
        env,
        draft: asRecord(data.draft),
      };
    }
    case 'finish':
      return { type: 'finish' };
    case 'abort':
      return { type: 'abort' };
    case 'error':
      return { type: 'error', message: str(data.message) ?? 'Something went wrong.' };
    default:
      return null;
  }
}

// Read an SSE response body and emit one domain event per frame. eventsource-parser handles the SSE
// framing (heartbeat comments, blank lines, chunk boundaries); we only map frames to domain events.
// Aborting the request rejects the read, which the caller maps to an abort event.
export async function parseSSEStream(response: Response, onEvent: (event: ChatStreamEvent) => void): Promise<void> {
  if (!response.body) {
    onEvent({ type: 'error', message: 'No response stream.' });
    return;
  }

  const parser = createParser({
    onEvent: (message) => {
      if (!message.data) {
        return;
      }
      try {
        const event = toEvent(JSON.parse(message.data) as RawFrame);
        if (event) {
          onEvent(event);
        }
      } catch {
        // Ignore a malformed frame rather than killing the whole stream.
      }
    },
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      parser.feed(decoder.decode(value, { stream: true }));
    }
  } finally {
    reader.releaseLock();
  }
}
